import { useEntryMutations } from "@app/hooks/useEntryMutations";
import { useState } from "react";
import type { EntryForDisplay } from "../interfaces/CapsuleEntry";
import type { CapsuleParty } from "../interfaces/CapsuleParty";
import type { ProjectSummary } from "../interfaces/ProjectSummary";
import { DateInput, FormatDateValue } from "./DateInput";
import { WithSpinner } from "./WithQuery";

interface EditEntryCardProps {
	original: EntryForDisplay;
	project: ProjectSummary;
	setEditing: (e: EntryForDisplay | null) => void;
	peopleData: CapsuleParty[];
}

export const EditEntryCard = ({
	original,
	project,
	setEditing,
	peopleData,
}: EditEntryCardProps) => {
	const [data, setData] = useState<EntryForDisplay>({
		...original,
		date: FormatDateValue(`${original.date}T${original.time}`),
	});

	const { putEntryQuery } = useEntryMutations();

	return (
		<WithSpinner loading={putEntryQuery.isPending}>
			<div className={"flex flex-col"}>
				<h3>Edit Timesheet Entry</h3>
				<div className={"flex flex-col padded-right"}>
					<label htmlFor={"activity-select"}>Activity</label>
					<select
						id={"activity-select"}
						className={"input"}
						value={data.activity}
						onChange={(e) => {
							const v = e.currentTarget.value;
							setData((prev) => {
								return { ...prev, activity: v };
							});
						}}
					>
						<option key={"svc"} value={"Service"}>
							Services
						</option>
						<option key={"phn"} value={"Phone"}>
							Phone
						</option>
						<option key={"sup"} value={"Support"}>
							Support
						</option>
						<option key={"trn"} value={"Training"}>
							Training
						</option>
					</select>
				</div>
				<div className={"flex flex-col padded-right"}>
					<label htmlFor={"date-time-of-work"}>Date/Time of Work:</label>
					<DateInput
						id={"date-time-of-work"}
						date={data.date}
						setDate={(v) =>
							setData((prev) => {
								return { ...prev, date: v };
							})
						}
					/>
				</div>
				<label htmlFor={"description-of-work"}>Description of Work:</label>
				<textarea
					id={"description-of-work"}
					className={"input"}
					value={data.details}
					onChange={(e) => {
						setData((prev) => {
							return { ...prev, details: e.target.value };
						});
					}}
				/>
				<label htmlFor={"requisitioner-select"}>
					Requisitioner (Optional):
				</label>
				<select
					id={"requisitioner-select"}
					className={"input"}
					value={data.requisitioner || ""}
					onChange={(e) => {
						if (e.target.value === "") {
							return setData((prev) => {
								return { ...prev, requisitioner: undefined };
							});
						}
						return setData((prev) => {
							return { ...prev, requisitioner: e.target.value };
						});
					}}
				>
					<option key={"-"} value={""}>
						-
					</option>
					{peopleData.map((p) => {
						const name = `${p.firstName} ${p.lastName} (${p.jobTitle})`;
						return (
							<option key={p.id} value={name}>
								{name}
							</option>
						);
					})}
				</select>
				<label htmlFor={"minutes-worked"}>Minutes Worked:</label>
				<input
					id={"minutes-worked"}
					value={data.minutes}
					className={"input"}
					onChange={(e) => {
						setData((prev) => {
							const v = Number(e.target.value);
							if (Number.isNaN(v)) {
								return prev;
							}
							return { ...prev, minutes: v };
						});
					}}
				/>
				<div className="flex-row">
					<button
						type={"button"}
						className={"button button-success"}
						onClick={() =>
							putEntryQuery.mutate({
								original: original,
								data: data,
								project: project,
								onSuccess: () => setEditing(null),
							})
						}
					>
						Update
					</button>
					<button
						type={"button"}
						className={"button button-primary"}
						onClick={() => setEditing(null)}
					>
						Cancel
					</button>
				</div>
			</div>
		</WithSpinner>
	);
};
